import VueRouter from 'vue-router';
import {connect} from 'redux-vue';
import {modalBeforeEnterFactory, scrollBehavior, setAppLang} from './helpers/tools';
import AxiosConfig from "./AxiosConfig";
import i18n from "./i18n"

import * as appActions from './actions/app';
import * as modulesActions from "./actions/modules";
import * as userActions from "./actions/user";

import Root from "./Root";
import HomePage from "./pages/HomePage";
import HelpPage from "./pages/HelpPage";
import AboutPage from "./pages/AboutPage";
import CoursesPage from "./pages/CoursesPage";
import CoursePage from "./pages/CoursePage";
import ProfilePage from "./pages/ProfilePage";
import ProfileEditPage from "./pages/ProfileEditPage";
import PasswordEditForm from "./components/PasswordEditForm";
import ProfileEditForm from "./components/ProfileEditForm";

import LoginModal from "./modals/LoginModal";
import RegisterModal from "./modals/RegisterModal";
import QuickviewModal from "./modals/QuickviewModal";
import TranscriptModal from "./modals/TranscriptModal";
import PasswordResetModal from "./modals/PasswordResetModal";
import PasswordResetResponseModal from "./modals/PasswordResetResponseModal";
import PasswordResetPage from "./pages/PasswordResetPage";

const routes = [
  {
    path: "/ukr/*",
    redirect: i18n.locale
  },
  {
    path: "/",
    redirect: i18n.locale
  },
  {
    path: "/:locale/course/:id",
    redirect: "/:locale/courses/:id"
  },
  {
    path: "/:locale",
    component: Root,
    children: [
      {
        path: '',
        name: 'HomePage',
        component: HomePage
      },
      {
        path: 'help',
        name: 'HelpPage',
        component: HelpPage
      },
      {
        path: 'about',
        name: 'AboutPage',
        component: AboutPage
      },
      {
        path: 'courses',
        name: 'CoursesPage',
        component: CoursesPage
      },
      {
        path: 'courses/transcript',
        name: 'TranscriptModal',
        components: { modal: TranscriptModal },
        beforeEnter: modalBeforeEnterFactory(CoursesPage)
      },
      {
        path: 'courses/:id/quickview',
        name: 'QuickviewModal',
        components: { modal: QuickviewModal },
        beforeEnter: modalBeforeEnterFactory(CoursesPage)
      },
      {
        path: 'courses/:id',
        name: 'CoursePage',
        component: CoursePage,
        meta: { requiresAuth: true },
      },
      {
        path: 'profile',
        name: 'ProfilePage',
        component: ProfilePage,
        meta: { requiresAuth: true },
      },
      {
        path: 'profile/edit',
        component: ProfileEditPage,
        meta: { requiresAuth: true },
        children: [
          {
            path: "",
            name: 'ProfileEditPage',
            component: ProfileEditForm,
          },
          {
            path: "password",
            name: 'PasswordEditPage',
            component: PasswordEditForm,
          },
        ]
      },
      {
        path: 'login',
        name: 'LoginModal',
        components: { modal: LoginModal },
        beforeEnter: modalBeforeEnterFactory(HomePage)
      },
      {
        path: 'register',
        name: 'RegisterModal',
        components: { modal: RegisterModal },
        beforeEnter: modalBeforeEnterFactory(HomePage)
      },
      {
        path: 'reset',
        name: 'PasswordResetModal',
        components: { modal: PasswordResetModal },
        beforeEnter: modalBeforeEnterFactory(HomePage)
      },
      {
        path: 'reset/response',
        name: 'PasswordResetResponseModal',
        components: { modal: PasswordResetResponseModal },
        beforeEnter: modalBeforeEnterFactory(HomePage)
      },
      {
        path: 'reset/:token',
        name: 'PasswordResetPage',
        component: PasswordResetPage
      },
      { path: '*', redirect: '/' }
    ]
  }
];

const RouterComponent = {
  props: ["user", "clearErrors", "fetchUserData", "fetchModules", "setLocale"],

  created() {
    if (this.user.token) {
      AxiosConfig.setAuthToken(this.user.token);
    }
    this.authGuard();
    this.clearErrors();
    this.fetchUserData();
    this.fetchModules();
  },

  watch: {
    '$route': 'authGuard',
    '$i18n.locale'(nextVal, val) {
      if(nextVal !== val) {
        this.fetchModules();
        if (this.user.token) {
          this.setLocale(nextVal);
        }
      }
    },
    user(nextVal, val) {
      if(val.token !== nextVal.token) {
        this.fetchModules();
      }
    }
  },

  methods: {
    authGuard() {
      const isAuthorized = !!(this.user.token && this.user.model);

      if (this.$route.meta.requiresAuth && !isAuthorized) {
        this.$router.push({
          name: 'LoginModal',
          query: { redirect: this.$route.name }
        });
      }
    }
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    return <router-view />;
  }
};

function mapStateToProps(state) {
  return {
    user: state.user,
  }
}

function mapActionToProps(dispatch) {
  return {
    clearErrors: () => dispatch(appActions.clearErrors()),
    fetchUserData: () => dispatch(appActions.fetchUserData()),
    fetchModules: () => dispatch(modulesActions.fetchModules()),
    setLocale: (locale) => dispatch(userActions.setLocale(locale))
  };
}

export default connect(mapStateToProps, mapActionToProps)(RouterComponent);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior,
  routes
});

router.beforeEach((to, from, next) => {
  if (to.params.locale === from.params.locale) {
    next();
    return;
  }
  const { locale } = to.params;
  setAppLang(locale);
  next();
});

export {router};
